import { Store } from 'rxjs-observable-store';
import { AuthStoreState } from './auth.store.state';
import { Injectable, OnDestroy } from '@angular/core';
import { StoreRequestStateUpdater } from 'src/app/shared/types/store-request-state-updater';
import { Observable, Subject } from 'rxjs';
import { AccountInfo } from '../types/account.info';
import { map, takeUntil, tap } from 'rxjs/operators';
import * as endpointHelpers from 'src/app/shared/helpers/endpoint.helpers';
import { UserToken } from '../types/user.token';
import { AuthEndpoint } from './auth.endpoint';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { TranslateService } from 'src/app/shared/services/translate.service';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import { WebsocketService } from '../../../shared/services/websocketService';
import * as hash from 'object-hash';
import { DotService } from '../../../shared/services/dot.service';
import { AccountNotification } from '../types/account.notification';
import { Action, Notification, NotificationAction } from '../../../features/account/profile/types/notification';

@Injectable()
export class AuthStore extends Store<AuthStoreState> implements OnDestroy {
  isLoggedIn$: Observable<boolean>;
  storeRequestStateUpdater: StoreRequestStateUpdater;
  private ngUnsubscribe$: Subject<undefined> = new Subject();

  constructor(
    public endpoint: AuthEndpoint,
    private router: Router,
    private translator: TranslateService,
    private translate: TranslatePipe,
    public websocketService: WebsocketService,
    private dotService: DotService
  ) {
    super(new AuthStoreState());
    this.isLoggedIn$ = this.state$.pipe(map((state) => state.isLoggedIn));
    this.storeRequestStateUpdater = endpointHelpers.getStoreRequestStateUpdater(this);
  }

  get user(): AccountInfo {
    return this.state.accountInfo;
  }

  set redirectUrl(url: string) {
    this.setState({ ...this.state, redirectUrl: url });
  }

  set userToken(userToken: UserToken) {
    const sessionId = hash([userToken.accessToken, moment().valueOf()]);
    this.setState({ ...this.state, sessionId, token: userToken });
    this.websocketService.connect(userToken.accessToken, sessionId);
    this.websocketService.receiver.observers.forEach((o) => o.complete());
    this.websocketService.receiver.observers = [];
    this.websocketService.receiver
      .pipe(
        tap((notification) => {
          this.addNotification(notification);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
    this.websocketService.actionReceiver.observers.forEach((o) => o.complete());
    this.websocketService.actionReceiver.observers = [];
    this.websocketService.actionReceiver
      .pipe(
        tap((action) => {
          this.addAction(action);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  public addNotification(notification: Notification): Notification {
    notification = this.processNotification(notification);
    if (notification === null) {
      return null;
    }
    this.loadAccountNotificationsStats()
      .pipe(
        tap(() => {
          this.addUnreadNotification();
          this.state.accountNotifications.notifications.unshift(notification);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
    return notification;
  }

  public addUnreadNotification() {
    this.unread = true;
  }

  public addAction(payload: Action) {
    if (NotificationAction[payload.action] === NotificationAction.READ.toString()) {
      const notification = this.state.accountNotifications.notifications.find((n) => n.id === payload.notificationId);
      if (notification) {
        notification.read = true;
      }
    } else if (NotificationAction[payload.action] === NotificationAction.READ_ALL.toString()) {
      for (const notification of this.state.accountNotifications.notifications) {
        notification.read = true;
      }
      this.state.accountNotifications.totalUnread = 0;
    } else if (NotificationAction[payload.action] === NotificationAction.CLEAR.toString()) {
      this.unread = false;
    }
  }

  init() {
    this.unread = false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  requestForToken(email: string, password: string) {
    this.endpoint
      .requestForToken(email, password, this.storeRequestStateUpdater)
      .pipe(
        tap((userToken: UserToken) => {
          this.state.isAfterLogin = true;
          this.saveTokenToLocalStorage(userToken);
          this.userToken = userToken;
          this.reloadAccountInfo().subscribe(
            () => {},
            () => {
              this.logout();
              this.router.navigate(['./login']);
            }
          );
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  refreshToken(): Observable<UserToken> {
    return this.endpoint.refreshToken(this.state.token.refreshToken, this.storeRequestStateUpdater).pipe(
      tap(
        (userToken: UserToken) => {
          this.saveTokenToLocalStorage(userToken);
          this.userToken = userToken;
        },
        (e) => {
          console.error(e);
        }
      ),
      takeUntil(this.ngUnsubscribe$)
    );
  }

  requestForgottenPassword(email: string) {
    return this.endpoint.requestForgottenPassword(email, this.storeRequestStateUpdater);
  }

  logout(): void {
    this.websocketService.disconnect();
    this.state.isLoggedIn = false;
    this.state.accountInfo = null;
    this.state.redirectUrl = '/';
    this.state.requests = {
      getAccountNotifications: {},
      getAccountInfo: {},
      requestForToken: {},
      refreshToken: {},
      updateLocale: {},
      requestForgottenPassword: {},
      getAccountNotificationsStats: {},
      clearUnreadNotifications: {},
    };
    this.state.token = {
      expirationDate: null,
      accessToken: '',
      refreshToken: '',
    };
    this.removeTokenFromLocalStorage();
    this.unread = false;
  }

  hasAuthority(authority: string): boolean {
    try {
      return this.state.accountInfo.permissions.includes(authority);
    } catch (e) {
      return false;
    }
  }

  hasRoleKey(roleKey: string): boolean {
    return this.state.accountInfo.roleKeys.includes(roleKey);
  }

  checkTokenInLocalStorage(): boolean {
    const expiration = localStorage.getItem('expiration');
    const expiresAt = moment(JSON.parse(expiration));
    if (moment().isBefore(expiresAt)) {
      const token = {
        accessToken: localStorage.getItem('access'),
        refreshToken: localStorage.getItem('refresh'),
        expirationDate: moment(expiresAt),
      };
      this.userToken = token;
      return true;
    }
    return false;
  }

  resolveAuthorizedUrl(url: string): string {
    if (this.state.accountInfo != null) {
      if (url.includes('profile')) {
        return url;
      }
      for (const mainMenuItem of this.state.accountInfo.mainMenu.menuItems) {
        if (
          mainMenuItem.state === url.substring(1) ||
          mainMenuItem.state + '/dashboard' === url.substring(1) ||
          mainMenuItem.state + '/view' === url.substring(1) ||
          mainMenuItem.state + '/edit' === url.substring(1) ||
          mainMenuItem.state + '/details' === url.substring(1) ||
          mainMenuItem.state + '/settings' === url.substring(1) ||
          mainMenuItem.state + '/new' === url.substring(1)
        ) {
          if (mainMenuItem.visible) {
            return url;
          }
        }
        if (mainMenuItem.children.length > 0) {
          for (const menuItem of mainMenuItem.children) {
            if (
              mainMenuItem.state + '/' + menuItem.state === url.substring(1) ||
              mainMenuItem.state + '/' + menuItem.state + '/view' === url.substring(1) ||
              mainMenuItem.state + '/' + menuItem.state + '/details' === url.substring(1) ||
              mainMenuItem.state + '/' + menuItem.state + '/edit' === url.substring(1) ||
              mainMenuItem.state + '/' + menuItem.state + '/settings' === url.substring(1) ||
              mainMenuItem.state + '/' + menuItem.state + '/new' === url.substring(1)
            ) {
              if (menuItem.visible) {
                return url;
              }
            }
          }
        }
      }
    }
    return '';
  }

  public reloadAccountInfo(forceLogout?: boolean): Observable<AccountInfo> {
    const $subject: Subject<AccountInfo> = new Subject();
    this.endpoint
      .getAccountInfo(forceLogout, this.storeRequestStateUpdater)
      .pipe(
        tap(
          (accountInfo: AccountInfo) => {
            if (accountInfo.roleKeys.includes('BM') || accountInfo.roleKeys.includes('MAN')) {
              this.websocketService.connect(this.state.token.accessToken, this.state.sessionId);
              this.websocketService.receiver.observers.forEach((o) => o.complete());
              this.websocketService.receiver.observers = [];
              this.websocketService.receiver
                .pipe(
                  tap((notification) => {
                    this.addNotification(notification);
                  }),
                  takeUntil(this.ngUnsubscribe$)
                )
                .subscribe();
              this.websocketService.actionReceiver.observers.forEach((o) => o.complete());
              this.websocketService.actionReceiver.observers = [];
              this.websocketService.actionReceiver
                .pipe(
                  tap((action) => {
                    this.addAction(action);
                  }),
                  takeUntil(this.ngUnsubscribe$)
                )
                .subscribe();
            }
            if (this.state.isAfterLogin) {
              if (accountInfo.roleGroup === 'EMPLOYEE') {
                this.state.redirectUrl = '/rh/imputations/search';
              }
              if (accountInfo.roleKeys.includes('HR')) {
                this.state.redirectUrl = '/rh/employees/search';
              } else {
                this.state.redirectUrl = '/dashboard';
              }
            }

            if (accountInfo.forceLogout === true) {
              this.refreshToken()
                .pipe(
                  tap((userToken) => {
                    this.saveTokenToLocalStorage(userToken);
                    this.reloadAccountInfo(true).subscribe(
                      (info) => {
                        $subject.next(info);
                        $subject.complete();
                      },
                      (e) => {
                        $subject.error(e);
                        $subject.complete();
                      }
                    );
                  }),
                  takeUntil(this.ngUnsubscribe$)
                )
                .subscribe();
              return;
            } else {
              this.handleGetAccountInfoResponse(accountInfo, () => {
                $subject.next(accountInfo);
                $subject.complete();
                if (accountInfo.roleKeys.includes('BM') || accountInfo.roleKeys.includes('MAN')) {
                  this.loadAccountNotifications();
                }
                if (
                  !this.state.isAfterLogin &&
                  accountInfo.roleKeys.includes('BM') &&
                  !this.state.redirectUrl.includes('motos')
                ) {
                  this.state.redirectUrl = '/motos';
                }
                this.router.navigateByUrl(this.state.redirectUrl).then((value) => {
                  if (!value) {
                    this.fetchRedirectUrl();
                    this.router.navigateByUrl(this.state.redirectUrl).then((res) => {
                      if (!res) {
                        this.logout();
                      }
                    });
                  }
                });
              });
            }
          },
          (e) => {
            $subject.error(e);
            $subject.next();
            $subject.complete();
          },
          () => {
            $subject.next();
            $subject.complete();
          }
        )
      )
      .subscribe();
    return $subject.asObservable();
  }

  resetState() {
    this.state.requests = {
      getAccountNotifications: {},
      getAccountInfo: {},
      requestForToken: {},
      refreshToken: {},
      updateLocale: {},
      requestForgottenPassword: {},
      getAccountNotificationsStats: {},
      clearUnreadNotifications: {},
    };
    this.state.accountNotifications = { size: 5, page: 0, notifications: [], total: 0, totalUnread: 0, unread: false };
    this.unread = false;
  }

  private handleGetAccountInfoResponse(accountInfo: AccountInfo, cb): void {
    localStorage.setItem('locale', accountInfo.locale.toLowerCase());
    this.translator.use(accountInfo.locale.toLowerCase() == 'fr' ? 'en' : accountInfo.locale.toLowerCase()).then(() => {
      const isAdminUser = accountInfo.roleGroup === 'ADMIN';
      const isEmployeeUser = accountInfo.roleGroup === 'EMPLOYEE';

      this.setState({
        ...this.state,
        accountInfo,
        isLoggedIn: true,
        isAdminUser,
        isEmployeeUser,
      });
      cb();
    });
  }

  private saveTokenToLocalStorage(userToken: UserToken) {
    localStorage.setItem('access', userToken.accessToken);
    localStorage.setItem('refresh', userToken.refreshToken);
    localStorage.setItem('expiration', JSON.stringify(userToken.expirationDate.valueOf()));
  }

  private removeTokenFromLocalStorage() {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('expiration');
  }

  private fetchRedirectUrl(): void {
    let baseRedirect = '';
    for (const menuItem of this.state.accountInfo.mainMenu.menuItems) {
      if (menuItem.children.length === 0 && menuItem.visible) {
        this.setState({ ...this.state, redirectUrl: menuItem.state });
        break;
      } else {
        baseRedirect = menuItem.state;
        for (const menuItemChild of menuItem.children) {
          if (menuItemChild.visible) {
            this.setState({
              ...this.state,
              redirectUrl: baseRedirect + '/' + menuItemChild.state,
            });
            break;
          }
        }
      }
    }
  }

  hasGroupRole(group: string): boolean {
    return this.state.accountInfo.roleGroup === group;
  }

  public markAllAsRead() {
    this.endpoint
      .markAllAsRead(this.storeRequestStateUpdater)
      .pipe(
        tap(
          (res: boolean) => {
            if (res) {
              for (const notification of this.state.accountNotifications.notifications) {
                notification.read = true;
              }
              this.loadAccountNotificationsStats().pipe(takeUntil(this.ngUnsubscribe$)).subscribe();
            }
          },
          () => {}
        )
      )
      .subscribe();
  }

  get unread(): boolean {
    return this.state.accountNotifications.unread;
  }

  set unread(unread) {
    this.dotService.toggle(unread);
    this.setState({ ...this.state, accountNotifications: { ...this.state.accountNotifications, unread } });
  }

  public clearUnreadNotifications() {
    const prevState = this.unread;
    this.unread = false;
    this.endpoint
      .clearUnreadNotifications(this.storeRequestStateUpdater)
      .pipe(
        tap(
          (res) => {
            if (!res) {
              this.unread = prevState;
            }
          },
          () => {
            this.unread = prevState;
          }
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  public markAsRead(uuid: string) {
    this.endpoint
      .markAsRead(uuid, this.storeRequestStateUpdater)
      .pipe(
        tap(
          (res: boolean) => {
            if (res) {
              for (const notification of this.state.accountNotifications.notifications) {
                if (notification.uuid === uuid) {
                  notification.read = true;
                }
              }
            }
            this.loadAccountNotificationsStats().pipe(takeUntil(this.ngUnsubscribe$)).subscribe();
          },
          () => {}
        )
      )
      .subscribe();
  }

  private loadAccountNotificationsStats(): Observable<AccountNotification> {
    return this.endpoint
      .getAccountNotificationsStats(
        this.state.accountNotifications.size,
        this.state.accountNotifications.page * this.state.accountNotifications.size,
        this.storeRequestStateUpdater
      )
      .pipe(
        tap(
          (accountNotifications: AccountNotification) => {
            this.state.accountNotifications.total = accountNotifications.total;
            this.state.accountNotifications.totalUnread = accountNotifications.totalUnread;
            this.unread = accountNotifications.totalUnread === 0 ? false : accountNotifications.unread;
          },
          () => {}
        )
      );
  }

  public loadAccountNotifications() {
    this.endpoint
      .getAccountNotifications(
        this.state.accountNotifications.size,
        this.state.accountNotifications.page,
        this.storeRequestStateUpdater
      )
      .pipe(
        tap(
          (accountNotifications: AccountNotification) => {
            moment.locale(this.translator.getLocale());

            accountNotifications.notifications = accountNotifications.notifications.filter(
              (notification) => this.processNotification(notification) !== null
            );
            accountNotifications.notifications = this.state.accountNotifications.notifications.concat(
              accountNotifications.notifications
            );
            if (accountNotifications.notifications.length <= accountNotifications.total) {
              accountNotifications.page += 1;
            }
            this.setState({ ...this.state, accountNotifications });
            this.unread = accountNotifications.totalUnread === 0 ? false : accountNotifications.unread;
          },
          () => {}
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private processNotification(notification: Notification) {
    return notification;
  }

  public logAction(actionType: string) {
    return this.endpoint.logAction(actionType, this.storeRequestStateUpdater).subscribe();
  }
}
